import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    List, Package, Tag, DollarSign, CalendarDays, BarChart3, Clock,
    Info, Search, ChevronLeft, CheckSquare, Square, XCircle, Loader2,
    ClipboardList, Building2, User, Hash, Box, Plus, Pencil
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useToast } from '../hooks/useToast';
import { fetchItemDetails } from '../apiHelper';
import { DataTable } from '../components/ui/DataTable'; // Adjust path if needed

const ItemDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();
    const listRef = useRef(null);

    const { users_id, selectedCatentryId, allCatalogEntries, store_id } = location.state || {};
    const [activeCatentryId, setActiveCatentryId] = useState(selectedCatentryId);
    const [activeTab, setActiveTab] = useState('overview');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [itemData, setItemData] = useState(null); // Full API response

    // Fetch full item details + transactions
    useEffect(() => {
        if (!activeCatentryId || !users_id) return;

        const today = new Date();
        const startDate = new Date();
        startDate.setDate(today.getDate() - 90); // Last 90 days by default

        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${dd}`;
        };

        const payload = {
            catentry_id: activeCatentryId,
            start_date: formatDate(startDate),
            end_date: formatDate(today),
            users_id,
            store_id: store_id || null
        };

        console.log(payload)

        setLoading(true);
        fetchItemDetails(payload)
            .then(response => {
                // console.log(response)
                setItemData(response);
            })
            .catch(err => {
                showToast("Failed to load item details.", "error");
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [activeCatentryId, users_id, showToast]);

    const currentItem = itemData?.item;
    const transactions = itemData?.transactions || [];

    // Find summary for sidebar
    const currentItemSummary = useMemo(() => {
        return allCatalogEntries?.find(item => item.catentry_id === activeCatentryId);
    }, [activeCatentryId, allCatalogEntries]);

    const filteredEntries = useMemo(() => {
        if (!allCatalogEntries) return [];
        const lower = searchTerm.toLowerCase();
        return allCatalogEntries.filter(entry =>
            entry.productname.toLowerCase().includes(lower) ||
            (entry.partnumber && entry.partnumber.toLowerCase().includes(lower))
        );
    }, [allCatalogEntries, searchTerm]);

    const handleItemSelect = useCallback((id) => {
        setActiveCatentryId(id);
    }, []);

    useEffect(() => {
        if (activeCatentryId && listRef.current) {
            const el = listRef.current.querySelector(`[data-id="${activeCatentryId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [activeCatentryId]);

    const tabs = [
        { key: 'overview', title: 'Overview', icon: <ClipboardList size={18} /> },
        { key: 'transactions', title: 'Transactions', icon: <BarChart3 size={18} /> },
        { key: 'history', title: 'History', icon: <Clock size={18} /> },
    ];

    if (!allCatalogEntries) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">Error: Missing required catalog data.</p>
                <Button onClick={() => navigate('/catalog')} variant="primary">
                    <ChevronLeft size={16} className="mr-2" /> Go to Product Catalogs
                </Button>
            </div>
        );
    }

    // Transaction columns
    const transactionColumns = [
        {
            key: 'date',
            label: 'Date',
            render: (value) => new Date(value).toLocaleDateString('en-GB'),
        },
        { key: 'invoice_number', label: 'Invoice #' },
        { key: 'customer_name', label: 'Customer' },
        {
            key: 'quantity_sold',
            label: 'Qty',
            render: (value) => Number(value).toLocaleString(),
        },
        {
            key: 'unit_price',
            label: 'Unit Price',
            render: (value, row) => `₦${Number(value || 0).toFixed(2)}`,
        },
        {
            key: 'total',
            label: 'Total',
            render: (value) => `₦${Number(value || 0).toFixed(2)}`,
        },
    ];

    return (
        <div className="flex h-[calc(100vh-100px)] bg-gray-100 p-6 rounded-lg shadow-inner gap-6">
            {/* Left Sidebar */}
            <Card className="w-full lg:w-1/4 bg-white rounded-lg shadow-md p-4 flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
                        <List size={20} className="text-gmail-blue" /> All Entries ({allCatalogEntries.length})
                    </h2>
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => navigate('/catalog')} size="sm" className="p-2" title="Back to Catalog List">
                            <ChevronLeft size={20} />
                        </Button>
                        <Button variant="primary" onClick={() => navigate('/catalog/create-item')} size="sm" className="p-2" title="Create New Item">
                            <Plus size={20} />
                        </Button>
                    </div>
                </div>
                <Input
                    type="text"
                    placeholder="Search list..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 text-sm mb-4"
                    icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                />
                <div ref={listRef} className="flex-1 overflow-y-auto custom-scrollbar space-y-1 pr-2">
                    {filteredEntries.length === 0 ? (
                        <div className="text-center text-sm text-text-secondary py-4">No matching items found.</div>
                    ) : (
                        filteredEntries.map(entry => (
                            <div
                                key={entry.catentry_id}
                                data-id={entry.catentry_id}
                                className={`flex items-center gap-2 p-3 rounded-lg transition-colors cursor-pointer ${entry.catentry_id === activeCatentryId
                                        ? 'bg-gmail-blue text-white shadow-md'
                                        : 'hover:bg-gray-100 text-text-primary'
                                    }`}
                                onClick={() => handleItemSelect(entry.catentry_id)}
                            >
                                <div className='w-4 h-4'>
                                    {entry.catentry_id === activeCatentryId ? (
                                        <CheckSquare size={16} className="text-white" />
                                    ) : (
                                        <Square size={16} className="text-gray-400" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate leading-snug">{entry.productname}</p>
                                    <p className={`text-xs truncate ${entry.catentry_id === activeCatentryId ? 'text-gray-200' : 'text-text-secondary'}`}>
                                        {entry.partnumber}
                                    </p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Card>

            {/* Right Content */}
            <div className="flex flex-col w-full lg:w-3/4 bg-white rounded-lg shadow-md">
                <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10 flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary">
                            {currentItem?.productname || 'Select a Product'}
                        </h1>
                        <p className="text-sm text-text-secondary">
                            Part No: {currentItem?.partnumber || 'N/A'}
                        </p>
                    </div>
                    {currentItem && (
                        <Button variant="secondary" size="sm" className="p-2" onClick={() => navigate('/catalog/edit-item', {
                            state: { 
                                item: currentItem,
                                catentry_id: activeCatentryId 
                            }
                        })}
                        title="Edit Item">
                            <Pencil size={18} />
                        </Button>
                    )}
                </div>

                <div className="flex border-b border-gray-200 px-4">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold transition-colors ${activeTab === tab.key
                                    ? 'border-b-2 border-g1mail-blue text-gmail-blue'
                                    : 'text-gray-600 hover:text-gray-800'
                                }`}
                        >
                            {tab.icon} {tab.title}
                        </button>
                    ))}
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="animate-spin h-10 w-10 text-gmail-blue mb-4" />
                            <p>Loading item details...</p>
                        </div>
                    ) : activeTab === 'overview' && currentItem ? (
                        <div className="space-y-8">
                            {/* Product Info */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <Hash size={18} className="text-gmail-blue" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Part Number</p>
                                            <p className="font-semibold">{currentItem.partnumber}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Tag size={18} className="text-purple-600" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Category</p>
                                            <p className="font-semibold">
                                                {currentItem.catgpenrels?.[0]?.category?.identifier || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Tag size={18} className="text-gmail-blue" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Catalog</p>
                                            <p className="font-semibold">
                                                {currentItem.catgpenrels?.[0]?.catalog?.identifier || 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Tag size={18} className="text-orange-600" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Tax Category</p>
                                            {currentItem.tax_categories && currentItem.tax_categories.length > 0 ? (
                                                <div className="space-y-1">
                                                    {currentItem.tax_categories.map((tax) => (
                                                        <div key={tax.taxcategory_id} className="font-semibold">
                                                            {tax.identifier} ({tax.code}) - {parseFloat(tax.rate).toFixed(2)} %
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="font-semibold text-gray-400">No tax applied</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-3">
                                        <DollarSign size={18} className="text-gmail-green" />
                                        <div>
                                            <p className="text-sm text-text-secondary">List Price</p>
                                            <p className="font-bold text-xl">
                                                ₦{Number(currentItem.listprice?.listprice || 0).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <User size={18} className="text-gmail-blue" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Owner</p>
                                            <p className="font-semibold">
                                                {currentItem.owner?.user?.first_name} {currentItem.owner?.user?.last_name}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <Box size={18} className="text-gmail-yellow" />
                                        <div>
                                            <p className="text-sm text-text-secondary">Unit of Measure</p>
                                            <p className="font-semibold">{currentItem.unitofmeasure?.name || 'Unit'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Inventory Lots */}
                            {currentItem.inventory_lots && currentItem.inventory_lots.length > 0 && (
                                <div>
                                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                        <Package size={20} className="text-gmail-blue" /> Inventory Lots
                                    </h3>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {currentItem.inventory_lots.map((lot, i) => (
                                            <Card key={i} className="p-4">
                                                <p className="text-sm text-text-secondary">Current Stock</p>
                                                <p className="text-2xl font-bold">{lot.current_quantity}</p>
                                                <p className="text-sm text-text-secondary mt-2">Cost: ₦{Number(lot.unit_cost).toFixed(2)}</p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Received: {new Date(lot.receipt_datetime).toLocaleDateString()}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Held At: <span className="text-text-700">{lot.store.identifier}</span>
                                                </p>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : activeTab === 'transactions' ? (
                        <div>
                            <h3 className="text-xl font-bold mb-6">All Transactions</h3>
                            <DataTable
                                columns={transactionColumns}
                                data={transactions}
                                title="Sales History"
                            />
                        </div>
                    ) : activeTab === 'history' ? (
                        <div className="text-center py-12 text-text-secondary">
                            <Clock size={48} className="mx-auto mb-4 text-gray-400" />
                            <p className="text-lg">Audit history coming soon...</p>
                        </div>
                    ) : (
                        <div className="text-center py-12 text-text-secondary">
                            <Info size={48} className="mx-auto mb-4 text-gray-400" />
                            <p>Select an item to view details</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ItemDetails;